<script setup lang="ts">
import type { UiTagProps } from '@afs/design-system/components/atoms/UiTag/interface.d.ts'
import { useData } from 'vike-vue/useData'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { FaqPageData } from './+data'
import { usePageContext } from 'vike-vue/usePageContext'
import { useFormatSeoContent } from '@/composables/useFormatSeoContent'
import { useGtmOnPageMounted } from '@/composables/useGtmTag'

const data = useData<FaqPageData>()
useFormatSeoContent(data.faq.seoTitle, data.faq.hero.subtitle)
useGtmOnPageMounted('other')

const pageContext = usePageContext()

const activeFilter = pageContext.urlParsed.search.activeFilter
  ? decodeURI(pageContext.urlParsed.search.activeFilter.toString())
  : (data.faq?.filterBlock?.filters[0]?.text ?? '')

const { t } = useI18n()

const selectedFilter = ref<UiTagProps | undefined>(
  data.faq?.filterBlock?.filters.find((filter) => filter.text === activeFilter) || undefined,
)

const displayedQuestions = computed(() => {
  if (selectedFilter.value?.id === 'all') {
    return data.faq?.questions || []
  } else {
    return data.faq?.questions?.filter((question) => question.tag === selectedFilter.value?.text) || []
  }
})

const handleSelectFilter = (filter: UiTagProps) => {
  selectedFilter.value = filter
}

const breadcrumb = {
  ariaLabel: t('breadcrumb.ariaLabel'),
  items: [
    { text: t('page.accueil'), to: '/' },
    { text: t('page.assistance'), to: '/faq' },
    {
      text:
        data.faq?.breadcrumbTitle && data.faq.breadcrumbTitle.length > 40
          ? `${data.faq.breadcrumbTitle.slice(0, 40)}...`
          : data.faq?.breadcrumbTitle,
    },
  ],
}
</script>

<template>
  <div
    v-if="data.faq"
    class="FAQ"
  >
    <molecules-ui-breadcrumb
      v-bind="breadcrumb"
      class="FAQ__breadcrumb"
    />
    <organisms-ui-hero v-bind="data.faq.hero" />
    <div class="container my-8">
      <section class="grid">
        <div
          v-if="selectedFilter"
          class="FAQ__filters mb-7"
        >
          <molecules-ui-filters-list
            v-if="data.faq.filterBlock"
            v-bind="data.faq.filterBlock"
            v-model="selectedFilter"
            @update:model-value="handleSelectFilter"
          />
        </div>
        <div class="FAQ__questions">
          <molecules-ui-faq-list :questions="displayedQuestions" />
        </div>
      </section>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use 'config/spaces';
@use 'config/colors';
@use 'utilities/mq';

.FAQ__breadcrumb {
  position: absolute;
  top: 0;
}

.FAQ__filters,
.FAQ__questions {
  grid-column: span 4;

  @include mq.media('>=medium') {
    grid-column: span 12;
  }

  @include mq.media('>=large') {
    grid-column: 2 / span 10;
  }
}
</style>
